import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { MenuSelect } from "components/Select/menuSelect";
import LinkModal from "components/LinkModal";
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useZustandStore from "zustandStore";

import Api from "./util/api";
import {
  cloneQosItems,
  createDefaultQosItems,
  isSameQosItems,
  normalizeQosItems,
} from "./util/func";

const NO_CHANGE = "no-change";

const cosOptions = [
  { label: "No Change", value: NO_CHANGE },
  ...Array.from({ length: 8 }, (_, value) => ({
    label: String(value),
    value,
  })),
];

const WiredQualityOfService = ({ showEditGeneral, onQosDefaultStateChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { auth, isAuthReady } = useAuthScope();
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);

  const [uuidList, _setUuidList] = useState({
    read: uuidv4(),
    update: uuidv4(),
  });
  const [wiredQosId, setWiredQosId] = useState(null);
  const [savedItems, setSavedItems] = useState(createDefaultQosItems);
  const [draftItems, setDraftItems] = useState(createDefaultQosItems);
  const [selectedDscp, setSelectedDscp] = useState([]);
  const [selectedCos, setSelectedCos] = useState(NO_CHANGE);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const draftItemsRef = useRef(draftItems);
  useEffect(() => {
    draftItemsRef.current = draftItems;
  }, [draftItems]);

  const selectedSet = useMemo(() => new Set(selectedDscp), [selectedDscp]);
  const hasChanges = useMemo(
    () => !isSameQosItems(draftItems, savedItems),
    [draftItems, savedItems]
  );
  const hasNonDefaultValue = useMemo(
    () => draftItems.some((item) => item.modified),
    [draftItems]
  );

  const applyDisabled =
    !showEditGeneral ||
    selectedDscp.length === 0 ||
    selectedCos === NO_CHANGE;
  const resetDisabled =
    !showEditGeneral ||
    !draftItems.some((item) => selectedSet.has(item.dscp) && item.modified);

  const fetchQosData = () => {
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(apiKey, uuidList.read, params);
  };

  useEffect(() => {
    if (isAuthReady) fetchQosData();
  }, [isAuthReady]);

  useEffect(() => {
    const readResponse = getData(uuidList.read);

    if (readResponse) {
      if (readResponse.data?.id) {
        setWiredQosId(readResponse.data.id);
      }
      const normalizedItems = normalizeQosItems(readResponse.data);
      setSavedItems(cloneQosItems(normalizedItems));
      setDraftItems(cloneQosItems(normalizedItems));
      onQosDefaultStateChange?.(normalizedItems.some((item) => item.modified));
      setSelectedDscp([]);
      setSelectedCos(NO_CHANGE);
      _setUuidList((prev) => ({ ...prev, read: uuidv4() }));
    }

    const updateResponse = getData(uuidList.update);

    if (updateResponse) {
      setSavedItems(cloneQosItems(draftItemsRef.current));
      onQosDefaultStateChange?.(draftItemsRef.current.some((item) => item.modified));
      setSelectedDscp([]);
      setSelectedCos(NO_CHANGE);
      _setUuidList((prev) => ({ ...prev, update: uuidv4() }));
    }
  }, [dataMap]);

  const handleToggleDscp = (dscp) => {
    if (!showEditGeneral) return;

    setSelectedDscp((prev) =>
      prev.includes(dscp)
        ? prev.filter((selected) => selected !== dscp)
        : [...prev, dscp]
    );
  };

  const handleApply = () => {
    if (applyDisabled) return;

    setDraftItems((prev) =>
      prev.map((item) => {
        if (selectedSet.has(item.dscp)) {
          const isModified = selectedCos !== item.defaultCos;
          return {
            ...item,
            cos: selectedCos,
            modified: isModified,
          };
        }
        return item;
      })
    );
    setSelectedDscp([]);
    setSelectedCos(NO_CHANGE);
  };

  const handleReset = () => {
    if (resetDisabled) return;

    setDraftItems((prev) =>
      prev.map((item) =>
        selectedSet.has(item.dscp) && item.modified
          ? {
            ...item,
            cos: item.defaultCos,
            modified: false,
          }
          : item
      )
    );
    setSelectedDscp([]);
    setSelectedCos(NO_CHANGE);
  };

  const handleCancel = () => {
    setDraftItems(cloneQosItems(savedItems));
    setSelectedDscp([]);
    setSelectedCos(NO_CHANGE);
  };

  const handleSave = () => {
    if (!hasChanges) return;

    if (serviceStatus?.services?.unityPro !== true && hasNonDefaultValue) {
      setIsLinkModalOpen(true);
      return;
    }

    const newUpdateUuid = uuidv4();
    _setUuidList((prev) => ({ ...prev, update: newUpdateUuid }));

    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.update, wiredQosId);

    sendRequest(apiKey, newUpdateUuid, params, {
      dscpValues: draftItems.map((item, index) => {
        const savedItem = savedItems[index];
        // 如果這一次沒有修改這格
        if (item.cos === savedItem.cos) {
          return savedItem.savedCos;
        }
        // 如果這次有修改，且被改回與預設值一樣 (如 Reset)，送出實際預設值
        if (item.cos === item.defaultCos) {
          return Number(item.defaultCos);
        }
        return Number(item.cos);
      }),
    });
  };

  return (
    <Box sx={{ padding: "20px 30px" }}>
      <Typography
        sx={{
          color: theme.palette.primary.main,
          fontSize: theme.typography.customFontSize[5],
          fontWeight: theme.typography.customFontWeight[1],
        }}
      >
        Global Settings
      </Typography>
      <Divider sx={{ margin: "20px 0" }} />

      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            color: theme.palette.primary.orange,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            mb: 2,
          }}
        >
          Quality of Service
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            mb: 3,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.orange,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            This feature is used to define the corresponding CoS priority for
            egress traffic management.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  border: `1px solid ${theme.palette.primary.orange}`,
                  borderRadius: "4px",
                }}
              />
              <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>
                Default
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.greenPrimary,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.white,
                    top: 5,
                    left: 5,
                  },
                }}
              />
              <Typography sx={{ fontSize: theme.typography.customFontSize[2] }}>
                Modified
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            Select DSCP values (0-63) to modify CoS assignments
          </Typography>

          <SolidButton
            width="120px"
            height="42px"
            disabled={resetDisabled}
            onClick={handleReset}
            sx={{
              borderRadius: theme.shape.customBorderRadius[1],
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[3],
              textTransform: "none",
            }}
          >
            Reset
          </SolidButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 100px)",
          gap: "20px",
        }}
      >
        {draftItems.map((item) => {
          const isSelected = selectedSet.has(item.dscp);
          const isModified = item.modified;
          return (
            <Button
              key={item.dscp}
              disableRipple
              onClick={() => handleToggleDscp(item.dscp)}
              sx={{
                position: "relative",
                width: 100,
                height: 60,
                minWidth: 0,
                alignItems: "flex-start",
                justifyContent: "flex-start",
                p: "7px 10px",
                border: `1px solid ${theme.palette.primary.orange}`,
                borderRadius: theme.shape.customBorderRadius[1],
                backgroundColor: isSelected
                  ? "#FFCA70"
                  : theme.palette.primary.orangeFourth,
                color: theme.palette.primary.orange,
                fontSize: theme.typography.customFontSize[3],
                fontWeight: theme.typography.customFontWeight[3],
                textTransform: "none",
                cursor: showEditGeneral ? "pointer" : "default",
                "&:hover": {
                  backgroundColor: showEditGeneral
                    ? "#FFCA70"
                    : theme.palette.primary.orangeFourth,
                },
              }}
            >
              {item.dscp}
              {isModified && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.greenPrimary,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.white,
                      top: 5,
                      left: 5,
                    },
                  }}
                />
              )}
              {item.cos !== null && (
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    left: 10,
                    bottom: 9,
                    width: 59,
                    height: 19,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    backgroundColor: "#7E3D00",
                    color: theme.palette.primary.white,
                    fontSize: theme.typography.customFontSize[1],
                    fontWeight: theme.typography.customFontWeight[3],
                    lineHeight: "19px",
                  }}
                >
                  Cos {item.cos}
                </Box>
              )}
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.greyPrimary,
            fontSize: theme.typography.customFontSize[2],
            fontWeight: theme.typography.customFontWeight[2],
          }}
        >
          {selectedDscp.length} Item(s) Selected
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[2],
            }}
          >
            Assign CoS
          </Typography>
          <MenuSelect
            value={selectedCos}
            onChange={(option) => setSelectedCos(option.value)}
            options={cosOptions}
            width="180px"
            disabled={!showEditGeneral}
            borderLeftWidth="8px"
            borderRightWidth="8px"
            borderBottomWidth="8px"
          />
          <OutlineButton
            width="150px"
            height="29px"
            iconStart={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            disabled={applyDisabled}
            onClick={handleApply}
            sx={{
              borderRadius: theme.shape.customBorderRadius[1],
              fontSize: theme.typography.customFontSize[1],
              whiteSpace: "nowrap",
              "& .MuiButton-startIcon": {
                marginRight: "4px",
                marginLeft: 0,
              },
            }}
          >
            Apply to Selected
          </OutlineButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 3,
          width: "100%",
          mt: 10,
        }}
      >
        <OutlineButton
          width="135px"
          height="42px"
          onClick={handleCancel}
          disabled={!showEditGeneral}
        >
          Cancel
        </OutlineButton>
        <SolidButton
          width="135px"
          height="42px"
          onClick={handleSave}
          disabled={!showEditGeneral || !hasChanges}
        >
          Save
        </SolidButton>
      </Box>

      <LinkModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        transparentBackdrop
        content="Quality of Service is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to save these changes."
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </Box>
  );
};

export default WiredQualityOfService;
